import React from "react";
import { MessageList } from "react-chat-elements";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
// import Linkify from "react-linkify";
import "react-chat-elements/dist/main.css";
import { format, formatDistanceToNowStrict } from "date-fns";
import NewColors from "../../themes/NewColors";
import SendMessage from "../SendMessage";

import useMessagesByGroup from "../../hooks/useMessagesByGroup";
import useEvents from "../../hooks/useEvents";

// Styled components using NewColors
const ChatContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  backgroundColor: NewColors.pageBackground,
  background: `linear-gradient(to bottom, ${NewColors.pageBackground} 0%, ${NewColors.cardBackground} 100%)`,
  position: "relative",
  overflow: "hidden",
  margin: "0 0 10px 0",
  padding: 0,
});

const SearchBarContainer = styled(Box)({
  padding: "8px 16px",
  borderBottom: `1px solid ${NewColors.triadicPower.primary}1A`,
  backgroundColor: NewColors.cardBackground,
  background: `linear-gradient(135deg, ${NewColors.cardBackground} 0%, ${NewColors.pageBackground} 100%)`,
  position: "relative",
  display: "flex",
  alignItems: "center",
  boxShadow: `0 2px 4px ${NewColors.utility.shadow}0D`,
});

const MessageListContainer = styled(Box)({
  flex: "1 1 auto",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
});

const SendMessageContainer = styled(Box)({
  borderTop: `1px solid ${NewColors.triadicPower.primary}1A`,
  backgroundColor: NewColors.cardBackground,
  background: `linear-gradient(135deg, ${NewColors.cardBackground} 0%, ${NewColors.pageBackground} 100%)`,
  paddingTop: "4px",
  paddingBottom: "4px",
  boxShadow: `0 -2px 4px ${NewColors.utility.shadow}0D`,
  flexShrink: 0,
  position: "sticky",
  bottom: 0,
  zIndex: 10,
  maxHeight: "80px",
  minHeight: "40px",
});

const SearchToggleContainer = styled(Box)({
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 2,
});

// Global styles for react-chat-elements using NewColors
const ChatStyles = styled("div")({
  "& .rce-mbox": {
    maxWidth: "80% !important",
    margin: "8px !important",
    boxShadow: `0 2px 8px ${NewColors.utility.shadow}1F !important`,
    borderRadius: "12px !important",
    border: `1px solid ${NewColors.triadicPower.primary}14 !important`,
  },
  "& .rce-mbox-right": {
    background: `linear-gradient(135deg, ${NewColors.triadicPower.primary}1A 0%, ${NewColors.triadicPower.primary}26 100%) !important`,
    border: `1px solid ${NewColors.triadicPower.primary}33 !important`,
  },
  "& .rce-mbox-left": {
    background: `linear-gradient(135deg, ${NewColors.cardBackground} 0%, ${NewColors.sectionBackground} 100%) !important`,
    border: `1px solid ${NewColors.border.medium} !important`,
  },
  "& .rce-mbox-text": {
    fontSize: "14px !important",
    padding: "10px 12px !important",
    lineHeight: "1.4 !important",
    color: `${NewColors.text.primary} !important`,
  },
  "& .rce-mbox-right .rce-mbox-text": {
    color: `${NewColors.triadicPower.primary} !important`,
  },
  "& .rce-mbox-title": {
    fontSize: "16px !important",
    fontFamily: "Lato !important",
    color: `${NewColors.triadicPower.primary} !important`,
    fontWeight: "600 !important",
    marginTop: "15px !important",
  },
  "& .rce-mbox-time": {
    bottom: "-20px !important",
    fontSize: "11px !important",
    color: `${NewColors.text.tertiary} !important`,
    fontWeight: "500 !important",
  },
  "& .rce-avatar-container": {
    marginRight: "8px !important",
  },
  "& .rce-avatar": {
    border: `2px solid ${NewColors.triadicPower.primary}33 !important`,
  },
  "& .message-list": {
    padding: "12px !important",
    height: "100% !important",
    maxHeight: "100% !important",
    background: `linear-gradient(to bottom, ${NewColors.pageBackground} 0%, ${NewColors.cardBackground} 100%) !important`,
    overflowY: "auto !important",
    overflowX: "hidden !important",
  },
  "& .rce-container-mbox": {
    padding: "8px 0 !important",
  },
  "& .rce-mbox-video": {
    maxWidth: "100% !important",
  },
  "& .rce-mbox-video-card": {
    maxWidth: "100% !important",
    borderRadius: "12px !important",
    overflow: "hidden !important",
  },
  "& .rce-mbox-video-card video": {
    maxWidth: "400px !important",
    borderRadius: "8px !important",
  },
  "& .rce-mbox-photo": {
    maxWidth: "100% !important",
    borderRadius: "8px !important",
    overflow: "hidden !important",
  },
  "& .rce-mbox-audio": {
    borderRadius: "20px !important",
    background: `linear-gradient(135deg, ${NewColors.triadicPower.accent}26 0%, ${NewColors.triadicPower.accent}1A 100%) !important`,
    border: `1px solid ${NewColors.triadicPower.accent}33 !important`,
  },
  "& .rce-mbox-right .rce-mbox-audio": {
    background: `linear-gradient(135deg, ${NewColors.triadicPower.primary}1A 0%, ${NewColors.triadicPower.primary}26 100%) !important`,
    border: `1px solid ${NewColors.triadicPower.primary}33 !important`,
  },
});

const ChatComponent = ({ uid, groupId }) => {
  const theme = useTheme();
  // Use useRef for messageListReference instead of createRef
  const messageListReference = React.useRef();
  const textInputRef = React.useRef(null);
  const searchTimeout = React.useRef();
  const events = useEvents();

  const [search, setSearch] = React.useState("");
  const [showSearch, setShowSearch] = React.useState(false);

  const { messages } = useMessagesByGroup({
    groupId,
    filter: search.length > 0 ? search : null,
  });

  // Memoize transformedMessages for performance
  const transformedMessages = React.useMemo(() => {
    if (!messages) return [];
    return messages.map((message) => {
      const messageDate = message.createdOn.toDate();
      const formattedDateTime = format(messageDate, "M/d/yyyy h:mm a");
      const relativeTime = formatDistanceToNowStrict(messageDate, {
        addSuffix: true,
      });
      const dateString = `${formattedDateTime} (${relativeTime})`;

      if (message.messageType === "image") {
        const width = 255;
        const height = 255 * (16 / 9);
        return {
          position: message.fromUID === uid ? "right" : "left",
          type: "photo",
          title: `${message.fromUser.firstName} ${message.fromUser.lastName}`,
          avatar: message.fromUser.avatarUrl || undefined,
          notch: false,
          data: {
            uri: message.imageUrl,
            width: width,
            height: height,
          },
          date: messageDate,
          dateString,
        };
      }
      if (message.messageType === "video") {
        return {
          position: message.fromUID === uid ? "right" : "left",
          type: "video",
          title: `${message.fromUser.firstName} ${message.fromUser.lastName}`,
          avatar: message.fromUser.avatarUrl || undefined,
          notch: false,
          data: {
            uri: message.videoUrl,
            videoURL: message.videoUrl,
            status: {
              autoDownload: true,
              click: false,
              loading: false,
              download: true,
            },
          },
          date: messageDate,
          dateString,
        };
      }
      if (message.messageType === "audio") {
        return {
          position: message.fromUID === uid ? "right" : "left",
          type: "audio",
          title: `${message.fromUser.firstName} ${message.fromUser.lastName}`,
          avatar: message.fromUser.avatarUrl || undefined,
          text: message.message,
          notch: false,
          data: {
            audioURL: message.audioUrl,
          },
          date: messageDate,
          dateString,
        };
      }
      return {
        position: message.fromUID === uid ? "right" : "left",
        type: "text",
        title: `${message.fromUser.firstName} ${message.fromUser.lastName}`,
        avatar: message.fromUser.avatarUrl || undefined,
        notch: false,
        text: (
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
            {message.message}
          </ReactMarkdown>
        ),
        date: messageDate,
        dateString,
      };
    });
  }, [messages, uid]);

  // Use a fixed height that's smaller to fit on screen
  const [containerHeight, setContainerHeight] = React.useState("80vh");

  React.useEffect(() => {
    // Function to update height based on window size
    const updateHeight = () => {
      // Using percentage of viewport height instead of fixed calculation
      setContainerHeight("80vh"); // Take up 80% of the viewport height
    };

    // Initial height set
    updateHeight();

    // Add resize listener
    window.addEventListener("resize", updateHeight);

    // Cleanup
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <ChatContainer
      className="chatParentBox"
      sx={{
        height: containerHeight,
        maxHeight: containerHeight,
      }}
    >
      {/* Search Bar - Only shown when showSearch is true */}
      {showSearch && (
        <SearchBarContainer>
          <TextField
            inputRef={textInputRef}
            placeholder="Search messages"
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            value={search}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch("");
                      events.logButtonClick({
                        buttonName: "Clear Search",
                        pageName: "Messages",
                        tabName: groupId,
                      });
                    }}
                    aria-label="Clear search text"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onChange={(event) => {
              const value = event.target.value;
              if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
              }
              searchTimeout.current = setTimeout(() => {
                events.logTextInput({
                  pageName: "Messages",
                  textboxName: "Search Messages",
                  text: value,
                });
                setSearch(value);
              }, 500);
            }}
          />
          <IconButton
            size="small"
            sx={{ ml: 1 }}
            onClick={() => setShowSearch(false)}
            aria-label="Close search bar"
          >
            <ClearIcon />
          </IconButton>
        </SearchBarContainer>
      )}

      {/* Message List */}
      <MessageListContainer>
        <ChatStyles>
          <MessageList
            className="message-list"
            toBottomHeight={"100%"}
            referance={messageListReference}
            lockable={false}
            dataSource={transformedMessages}
            messageBoxStyles={{
              marginInline: 0,
            }}
            style={{
              height: "100%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          />
        </ChatStyles>
      </MessageListContainer>

      {/* Send Message Component */}
      <SendMessageContainer>
        <SendMessage uid={uid} groupId={groupId} />
      </SendMessageContainer>

      {/* Search Toggle Button - only show when search bar is closed */}
      {!showSearch && (
        <SearchToggleContainer>
          <IconButton
            size="small"
            sx={{
              backgroundColor: `${NewColors.triadicPower.primary}1A`,
              backdropFilter: "blur(8px)",
              border: `1px solid ${NewColors.triadicPower.primary}33`,
              color: NewColors.triadicPower.primary,
              "&:hover": {
                backgroundColor: `${NewColors.triadicPower.primary}26`,
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease-in-out",
            }}
            onClick={() => setShowSearch(true)}
            aria-label="Open search"
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </SearchToggleContainer>
      )}
    </ChatContainer>
  );
};

export default ChatComponent;
